import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Plus, Sunrise, Sun, Moon, BookOpen, Check, ChevronRight, X, Headphones } from 'lucide-react';

const WINDOW_ICON = { morning: Sunrise, midday: Sun, evening: Moon, any: Clock };
const UNFILED = 'Everything else';

/** Inside an open family, his own groups ("Emotional Well-Being") keep their shape. */
const subgroups = (list) => {
  const groups = new Map();
  list.forEach((card) => {
    const key = (card.subcategory || '').trim();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(card);
  });
  return [...groups.entries()].sort(([a], [b]) => (!a) - (!b) || a.localeCompare(b));
};

const Card = ({ card, onAdopt }) => {
  const Icon = WINDOW_ICON[card.suggested_window] || Clock;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2
                    hover:border-amber-300/30 transition-colors duration-200"
         data-testid={`library-card-${card.id}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-white leading-snug">{card.title}</h3>
        <button onClick={() => onAdopt(card)} disabled={card.adopted}
                className="shrink-0 inline-flex items-center gap-1 rounded-full border
                           border-amber-300/40 bg-amber-300/10 px-2.5 py-1 text-[11px]
                           text-amber-100 hover:bg-amber-300/20 disabled:opacity-40
                           transition-colors"
                data-testid={`library-adopt-${card.id}`}>
          {card.adopted
            ? <><Check className="w-3 h-3" /> Added</>
            : <><Plus className="w-3 h-3" /> Add</>}
        </button>
      </div>
      {card.description && (
        <p className="text-xs text-white/50 leading-relaxed">{card.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/40">
        <span className="inline-flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {card.suggested_window === 'any' ? 'Any window' : card.suggested_window}
        </span>
        {card.duration_minutes && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {card.duration_minutes} min
          </span>
        )}
        {card.audio_url && (
          <span className="inline-flex items-center gap-1 text-amber-200/70"
                data-testid={`library-recording-${card.id}`}>
            <Headphones className="w-3 h-3" /> Guided recording
          </span>
        )}
        {card.source?.label && (
          <Link to={card.source.module_id ? `/dashboard/modules/${card.source.module_id}` : '#'}
                className="inline-flex items-center gap-1 text-cyan-200/70 hover:text-cyan-100
                           transition-colors"
                data-testid={`library-source-${card.id}`}>
            <BookOpen className="w-3 h-3" /> From {card.source.label}
          </Link>
        )}
      </div>
    </div>
  );
};

/**
 * The library on desktop: categories as tiles, and the chosen one opening across the full width
 * beneath them. With a card for every program exercise, a single list would run for pages.
 */
export const CategoryBrowser = ({ cards = [], onAdopt, onCreate }) => {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState(null);

  const term = query.trim().toLowerCase();
  const matches = cards.filter((c) =>
    !term || `${c.title} ${c.description} ${c.category}`.toLowerCase().includes(term));

  const groups = matches.reduce((acc, card) => {
    const key = card.category?.trim() || UNFILED;
    (acc[key] = acc[key] || []).push(card);
    return acc;
  }, {});
  const names = Object.keys(groups).sort((a, b) =>
    a === UNFILED ? 1 : b === UNFILED ? -1 : a.localeCompare(b));

  const open = term ? null : chosen;
  const shown = term ? matches : (open ? groups[open] || [] : []);

  return (
    <div className="space-y-4" data-testid="category-browser">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold text-white">Ritual library</h2>
        <div className="flex items-center gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search every ritual"
                 className="w-56 rounded-lg border border-white/10 bg-black/30 px-3 py-1.5
                            text-xs text-white placeholder-white/25 focus:outline-none
                            focus:border-amber-300/60"
                 data-testid="ritual-library-search" />
          <button onClick={onCreate}
                  className="inline-flex items-center gap-1.5 rounded-full border
                             border-dashed border-white/20 px-3 py-1.5 text-xs text-white/70
                             hover:text-white hover:border-amber-300/50 transition-colors"
                  data-testid="ritual-create-custom">
            <Plus className="w-3.5 h-3.5" /> Write your own
          </button>
        </div>
      </div>

      {names.length === 0 ? (
        <p className="text-sm text-white/40 py-8 text-center" data-testid="ritual-library-empty">
          {cards.length
            ? 'Nothing matches that.'
            : 'Ritual cards from the program will gather here as you reach their sections.'}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {names.map((name) => {
            const all = groups[name].every((c) => c.adopted);
            return (
              <button key={name} onClick={() => setChosen(open === name ? null : name)}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left
                                  transition-colors duration-200
                        ${open === name
                          ? 'border-amber-300/50 bg-amber-300/[0.07]'
                          : 'border-white/[0.08] bg-white/[0.02] hover:border-white/25'}`}
                      data-testid={`library-category-toggle-${name}`}>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-white truncate">{name}</span>
                  <span className="block text-[11px] text-white/35">
                    {groups[name].length} ritual{groups[name].length === 1 ? '' : 's'}
                    {all ? ' · all added' : ''}
                  </span>
                </span>
                <ChevronRight className={`w-4 h-4 transition-transform duration-200
                  ${open === name ? 'rotate-90 text-amber-200' : 'text-white/30'}`} />
              </button>
            );
          })}
        </div>
      )}

      {shown.length > 0 && (
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 space-y-4"
             data-testid="library-category-open">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-white">
              {term ? `${shown.length} matching ritual${shown.length === 1 ? '' : 's'}` : open}
            </h3>
            {!term && (
              <button onClick={() => setChosen(null)}
                      className="text-white/40 hover:text-white transition-colors"
                      data-testid="library-category-close">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="space-y-5">
            {subgroups(shown).map(([group, list]) => (
              <div key={group} className="space-y-3">
                {group && (
                  <h4 className="text-[11px] uppercase tracking-wider text-amber-200/60"
                      data-testid={`library-subgroup-${group}`}>
                    {group} · {list.length}
                  </h4>
                )}
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {list.map((card) => <Card key={card.id} card={card} onAdopt={onAdopt} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryBrowser;
